import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wrench, ArrowRight, AlertCircle, User2 } from "lucide-react";

export default function ClientLoginPage() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (user && user.role === "cliente" && user.is_approved) {
            navigate("/minha-conta", { replace: true });
        } else if (user && (user.role === "admin" || user.role === "operador")) {
            navigate("/dashboard", { replace: true });
        }
    }, [user, navigate]);

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            navigate("/minha-conta", { replace: true });
        } else {
            setError(res.error || "Erro ao iniciar sessão");
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6 py-10">
            <div className="w-full max-w-md">
                <Link to="/" className="mb-8 inline-flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#FF3B30] text-white">
                        <Wrench className="h-5 w-5" />
                    </div>
                    <span className="font-['Outfit'] text-lg font-semibold">Oficina Moto</span>
                </Link>

                <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-500">
                    <User2 className="h-3.5 w-3.5" />
                    Área do Cliente
                </div>
                <h1 className="mb-2 font-['Outfit'] text-3xl font-semibold tracking-tight text-zinc-900">
                    Bem-vindo de volta.
                </h1>
                <p className="mb-8 text-sm text-zinc-600">
                    Aceda à sua conta para agendar revisões e gerir os seus veículos.
                </p>

                <form onSubmit={onSubmit} className="space-y-5" data-testid="client-login-form">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-700">Email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="mt-1.5 h-11 rounded-sm border-zinc-300"
                            data-testid="client-login-email"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-700">Palavra-passe</Label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="mt-1.5 h-11 rounded-sm border-zinc-300"
                            data-testid="client-login-password"
                        />
                    </div>

                    {error ? (
                        <div className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="client-login-error">
                            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    ) : null}

                    <Button
                        type="submit"
                        disabled={loading}
                        className="h-11 w-full rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        data-testid="client-login-submit"
                    >
                        {loading ? "A entrar..." : (
                            <span className="inline-flex items-center gap-2">
                                Entrar
                                <ArrowRight className="h-4 w-4" />
                            </span>
                        )}
                    </Button>
                </form>

                <div className="mt-8 border-t border-zinc-200 pt-6 text-sm text-zinc-600">
                    Ainda não tem conta?{" "}
                    <Link to="/registar" className="font-semibold text-[#FF3B30] hover:underline" data-testid="link-register">
                        Registar agora
                    </Link>
                </div>
                <div className="mt-3 text-center text-xs text-zinc-500">
                    <Link to="/" className="hover:text-zinc-900">← Voltar à página inicial</Link>
                </div>
            </div>
        </div>
    );
}
